import _user from '@H/user.js';
import store from '@VX/store';
import route from '@H/route';
import {
    goRoute,
    notVerified,
    finishedEnrolment,
    notAuthenticated,
    permittedForStage,
    isLastStage
} from '@H/enrolment';
import { stageRoutes } from '@H/marketing';
import axios from 'axios';

export default {
    async login(to, from, next) {
        try {
            let user = await store.dispatch('Auth/getUser');

            if (!user.email_verified_at) {
                return _user.mustBeVerified(user, next);
            }

            if (
                !user.profile.finished_enrolment &&
                !user.hasActiveSubscription
            ) {
                return _user.mustHaveFinishedEnrolment(user, next);
            }

            next({ name: 'landing' });
        } catch (err) {
            next();
        }
    },
    async landing(to, from, next) {
        try {
            let user = await store.dispatch('Auth/getUser');

            if (!user.email_verified_at) {
                return _user.mustBeVerified(user, next);
            }

            if (
                !user.profile.finished_enrolment &&
                !user.hasActiveSubscription
            ) {
                return _user.mustHaveFinishedEnrolment(user, next);
            }

            store.dispatch('setLanguage', user.language, { root: true });
            store.commit('Auth/firstName', user.first_name);
            next();
        } catch (err) {
            next({ name: 'login' });
        }
    },
    async register(to, from, next) {
        try {
            let user = await store.dispatch('Auth/getUser');

            if (!user.email_verified_at) {
                return _user.mustBeVerified(user, next);
            }

            if (!user.profile.finished_enrolment) {
                return _user.mustHaveFinishedEnrolment(user, next);
            }

            next({ name: 'landing' });
        } catch (err) {
            next();
        }
    },
    async nonStepCheck(to, from, next) {
        try {
            const user = await store.dispatch('Auth/getUser');

            if (user == null) {
                notAuthenticated(next);
            }

            const actualUser = to.params.user || user;

            store.dispatch('Enrolment/setUser', actualUser);

            // Not verified email
            if (actualUser.email_verified_at == null) {
                return notVerified(actualUser.email, next);
            }

            // Has finished enrolment
            if (actualUser.profile.finished_enrolment && !isLastStage(to)) {
                return finishedEnrolment(actualUser, next);
            }

            // Has an active subscription
            if (actualUser.hasActiveSubscription && !isLastStage(to)) {
                return next({ name: 'landing' });
            }

            next();
        } catch (err) {
            notAuthenticated(next);
        }
    },
    async enrolmentCheck(to, from, next) {
        try {
            let user = await store.dispatch('Auth/getUser');

            if (user == null) {
                notAuthenticated(next);
            }

            let actualUser = to.params.user || user;

            store.dispatch('Enrolment/setUser', actualUser);

            // Not verified email
            if (actualUser.email_verified_at == null) {
                return notVerified(actualUser.email, next);
            }

            // Has finished enrolment
            if (actualUser.profile.finished_enrolment && !isLastStage(to)) {
                return finishedEnrolment(actualUser, next);
            }

            // Has an active subscription
            if (actualUser.hasActiveSubscription && !isLastStage(to)) {
                return next({ name: 'landing' });
            }

            const { go, on } = permittedForStage(actualUser, to);
            // If we can go to the route (we have been validated for the route).
            if (go) {
                // Go to route if we're not already on it.
                if (go !== on) {
                    return goRoute(go, next);
                }
                // Otherwise we reset to the first proper stage.
            } else {
                store.commit('Enrolment/resetCurrentStage');
                return next({ name: 'enrolment-personal', params: { user } });
            }

            next();
        } catch (err) {
            notAuthenticated(next);
        }
    },
    async mySubscriptions(to, from, next) {
        // We need to have a selected upgrade in the state for subsequent screens
        if (
            to.name != 'my-subscriptions' &&
            !store.state.Widget.selectedUpgrade.subscriptionId
        ) {
            store.commit('MySubscriptions/mySubscriptionsInitial', false);
            return next({ name: 'my-subscriptions' });
        }

        const hasLoaded =
            store.getters['MySubscriptions/hasLoadedMySubscriptions'];

        if (!hasLoaded) {
            try {
                let user = await store.dispatch('Auth/getUser');

                if (!user.profile.finished_enrolment) throw 'User must enrol';
            } catch (err) {
                // A full page reload is kinda gross but the easiest fix since we have different server-side layout files for this section & enrolment
                return (window.location.href = '/');
            }

            // Any async calls we need to make before hiding the loader should be put into this requests array
            let promises = [
                store.dispatch(
                    'MySubscriptions/getSubscriptions',
                    route('mysubs.get-subscriptions')
                ),
                store.dispatch(
                    'MySubscriptions/getPaymentAttempts',
                    route('mysubs.payment-attempts')
                )
            ];

            Promise.all(promises)
                .then(res => {
                    // res[0] is the user's subscriptions (excluding cancelled ones). There are
                    // multiple active states, but only the 'Created' subscriptions returned here
                    // will not be active so we want to filter those out to check if the user can
                    // view this page.
                    const hasActive = res[0].filter(
                        sub => sub.status !== 'Created'
                    ).length;
                    if (hasActive) {
                        return (window.location.href = '/');
                    }
                    store.commit(
                        'MySubscriptions/mySubscriptionsInitial',
                        true
                    );
                    return next();
                })
                .catch(err => {
                    return (window.location.href = '/');
                });
        } else {
            return next();
        }
    },
    logout(to, from, next) {
        return store.dispatch('Auth/logout');
    },
    verify(to, from, next) {
        if (store.state.Auth.email == '') {
            return store.dispatch('Auth/logout');
        }

        next();
    },
    verifyCallback(to, from, next) {
        if (typeof to.query.id == 'undefined') {
            return store.dispatch('Auth/logout');
        }

        return next();
    },
    resetEmailSent(to, from, next) {
        let { email } = to.params;

        if (!email) {
            return store.dispatch('Auth/logout');
        }

        next();
    },
    async passwordReset(to, from, next) {
        try {
            await axios.post(route('password.validate-reset-token'), {
                email: to.query.email
            });

            next();
        } catch (err) {
            return next({
                name: 'login',
                params: {
                    flash: {
                        message: $gettext('Your password reset link has expired')
                    }
                }
            });
        }
    },
    async marketingStep(to, from, next) {
        try {
            const user = await store.dispatch('Marketing/getUser');

            if (user === null) {
                notAuthenticated(next);
            }

            const actualUser = to.params.user || user;

            store.dispatch('Marketing/setUser', actualUser);

            store.commit('setFormSubmitting', false, { root: true });

            // Has an active subscription
            if (actualUser.hasActiveSubscription && actualUser.profile.finished_enrolment) {
                return next({ name: 'landing' });
            }

            const { profile: { current_stage: currentStage } } = user;
            const stageKeys = Object.keys(stageRoutes);
            const stageVals = Object.values(stageRoutes);

            const attemptedStageIndex = stageVals.indexOf(to.name);
            const currentStageIndex = stageKeys.indexOf(currentStage);

            if (currentStage && attemptedStageIndex > currentStageIndex) {
                next({ name: currentStageIndex });
            }

            next();
        } catch (err) {
            notAuthenticated(next);
        }
    }
};



// WEBPACK FOOTER //
// ./resources/assets/js/router/guards.js